import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import config from 'config/Config';
import intl from 'react-intl-universal';
import { SUPPORT_POPUP_OPTIONS, SLICE_STATUS } from 'utils/constants';
import { HELPER_CENTER_LINK } from 'utils/constants';
import { PATHS } from 'utils/constants';
import { AppSideNavProps, NavItem } from 'Organisms/AppSideNav';
import NavItemContent from 'Organisms/AppSideNav/components/NavItem/NavItemContent';
import homeIcon from 'assets/icons/nav/home.svg';
import dashboardIcon from 'assets/icons/nav/dashboard.svg';
import insightIcon from 'assets/icons/nav/insight.svg';
import intakeIcon from 'assets/icons/nav/request.svg';
import projectIcon from 'assets/icons/nav/project.svg';
import taskIcon from 'assets/icons/nav/task.svg';
import designIcon from 'assets/icons/nav/design.svg';
import teamIcon from 'assets/icons/nav/team.svg';
import programIcon from 'assets/icons/nav/program.svg';
import schedulerIcon from 'assets/icons/nav/event.svg';
import budgetIcon from 'assets/icons/nav/budget.svg';
import evaluationIcon from 'assets/icons/nav/evaluation.svg';
import strategyIcon from 'assets/icons/nav/strategy.svg';
import settingIcon from 'assets/icons/nav/setting.svg';
import chatIcon from 'assets/icons/nav/chat.svg';
import albumIcon from 'assets/icons/nav/album.svg';
import certIcon from 'assets/icons/nav/cert.svg';
import communityIcon from 'assets/icons/nav/community.svg';
import { showMessenger } from 'Services/intercom';
import NavItemLink from 'Organisms/AppSideNav/components/NavItem/NavItemLink';
import { NavPage, NavItem as StoreNavItem } from 'types/store/nav';
import {
  selectNavItems,
  selectNavItemsStatus,
} from 'state/Navigation/navigationSlice';

const canShowPage = (navItems: StoreNavItem[], page: NavPage): boolean => {
  const navItem = navItems.find((item) => item.page === page);
  return navItem?.canShow ?? false;
};

const useAppSideNav = (): AppSideNavProps => {
  const location = useLocation();

  const [itemGroups, setItemGroups] = useState<NavItem[][]>([[]]);
  const navItems = useSelector(selectNavItems);
  const navItemsStatus = useSelector(selectNavItemsStatus);

  const isPathActive = useCallback(
    (itemHref: string) => {
      if (itemHref === PATHS.ROOT) {
        return location.pathname === PATHS.ROOT;
      }
      return location.pathname.startsWith(itemHref);
    },
    [location.pathname]
  );

  const buildItemGroups = useCallback(() => {
    setItemGroups([
      [
        ...(canShowPage(navItems, NavPage.HOME)
          ? [
              {
                iconSrc: homeIcon,
                label: intl.get('SIDEBAR.HOME'),
                href: PATHS.ROOT,
                activeBorderColor: '#7f94c8',
                isActive: isPathActive(PATHS.ROOT),
                isExternalLink: false,
              },
            ]
          : []),

        ...(canShowPage(navItems, NavPage.DASHBOARDS)
          ? [
              {
                iconSrc: dashboardIcon,
                label: intl.get('SIDEBAR.DASHBOARD'),
                href: PATHS.DASHBOARDS_LIST_PAGE,
                activeBorderColor: '#7f94c8',
                isActive: isPathActive(PATHS.DASHBOARDS_LIST_PAGE),
                isExternalLink: false,
              },
            ]
          : []),

        ...(canShowPage(navItems, NavPage.INSIGHTS)
          ? [
              {
                iconSrc: insightIcon,
                label: intl.get('SIDEBAR.INSIGHTS'),
                href: PATHS.INSIGHTS,
                activeBorderColor: '#7f94c8',
                isActive: isPathActive(PATHS.INSIGHTS),
                isExternalLink: false,
              },
            ]
          : []),
      ],
      [
        ...(canShowPage(navItems, NavPage.REQUESTS)
          ? [
              {
                iconSrc: intakeIcon,
                label: intl.get('SIDEBAR.INTAKE'),
                href: PATHS.REQUESTS_LIST_PAGE,
                activeBorderColor: '#c06892',
                isActive: isPathActive(PATHS.REQUESTS_LIST_PAGE),
                isExternalLink: false,
              },
            ]
          : []),
        ...(canShowPage(navItems, NavPage.PROGRAMS)
          ? [
              {
                iconSrc: programIcon,
                label: intl.get('SIDEBAR.PROGRAMS'),
                href: PATHS.PROGRAMS_LIST_PAGE,
                activeBorderColor: '#c06892',
                isActive: isPathActive(PATHS.PROGRAMS_LIST_PAGE),
                isExternalLink: false,
              },
            ]
          : []),

        ...(canShowPage(navItems, NavPage.PROJECTS)
          ? [
              {
                iconSrc: projectIcon,
                label: intl.get('SIDEBAR.PROJECTS'),
                href: PATHS.PROJECTS_LIST_PAGE,
                activeBorderColor: '#c06892',
                isActive: isPathActive(PATHS.PROJECTS_LIST_PAGE),
                isExternalLink: false,
              },
            ]
          : []),
        ...(canShowPage(navItems, NavPage.TASKS)
          ? [
              {
                iconSrc: taskIcon,
                label: intl.get('SIDEBAR.TASKS'),
                href: PATHS.TASKS_LIST_PAGE,
                activeBorderColor: '#c06892',
                isActive: isPathActive(PATHS.TASKS_LIST_PAGE),
                isExternalLink: false,
              },
            ]
          : []),
        ...(canShowPage(navItems, NavPage.DESIGN)
          ? [
              {
                iconSrc: designIcon,
                label: intl.get('SIDEBAR.DESIGN'),
                href: config.get('designURL'),
                activeBorderColor: '#c06892',
                isActive: isPathActive(config.get('designURL')),
                isExternalLink: true,
              },
            ]
          : []),
      ],
      [
        ...(canShowPage(navItems, NavPage.SCHEDULE)
          ? [
              {
                iconSrc: schedulerIcon,
                label: intl.get('SIDEBAR.SCHEDULE'),
                href: PATHS.LEARNING_SCHEDULE_PAGE,
                activeBorderColor: '#f4b09a',
                isActive: isPathActive(PATHS.LEARNING_SCHEDULE_PAGE),
                isExternalLink: false,
              },
            ]
          : []),
      ],
      [
        ...(canShowPage(navItems, NavPage.STRATEGY)
          ? [
              {
                iconSrc: strategyIcon,
                label: intl.get('SIDEBAR.STRATEGY'),
                href: PATHS.STRATEGY_GOALS,
                activeBorderColor: '#55ac9b',
                isActive: isPathActive(PATHS.STRATEGY_GOALS),
                isExternalLink: false,
              },
            ]
          : []),
        ...(canShowPage(navItems, NavPage.BUDGET)
          ? [
              {
                iconSrc: budgetIcon,
                label: intl.get('SIDEBAR.BUDGETS'),
                href: PATHS.BUDGETS,
                activeBorderColor: '#55ac9b',
                isActive: isPathActive(PATHS.BUDGETS),
                isExternalLink: false,
              },
            ]
          : []),
        ...(canShowPage(navItems, NavPage.TEAMS)
          ? [
              {
                iconSrc: teamIcon,
                label: intl.get('SIDEBAR.TEAMS'),
                href: PATHS.TEAMS,
                activeBorderColor: '#55ac9b',
                isActive: isPathActive(PATHS.TEAMS),
                isExternalLink: false,
              },
            ]
          : []),
      ],
      [
        ...(canShowPage(navItems, NavPage.EVALUATIONS)
          ? [
              {
                iconSrc: evaluationIcon,
                label: intl.get('ENTITIES.EVALUATION', { num: 2 }),
                href: PATHS.EVALUATIONS,
                activeBorderColor: '#5da1dc',
                isActive: isPathActive(PATHS.EVALUATIONS),
                isExternalLink: false,
              },
            ]
          : []),
      ],
      [
        ...(canShowPage(navItems, NavPage.SETTINGS)
          ? [
              {
                iconSrc: settingIcon,
                label: intl.get('SIDEBAR.SETTINGS'),
                href: PATHS.SETTINGS,
                activeBorderColor: '#a0b0b5',
                isActive: isPathActive(PATHS.SETTINGS),
                isExternalLink: false,
              },
            ]
          : []),
      ],
    ]);
  }, [navItems, isPathActive]);

  useEffect(() => {
    if (navItemsStatus === SLICE_STATUS.SUCCESS) {
      buildItemGroups();
    }
  }, [navItemsStatus, buildItemGroups]);

  const appSideNavProps = useMemo<AppSideNavProps>(() => {
    return {
      itemGroups: itemGroups,
      renderItem: (item: NavItem, isExpanded: boolean) => {
        if (!item.isExternalLink) {
          return (
            <Link
              to={item.href}
              rel='noreferrer'
              className='w-full h-full'
              aria-current={item.isActive ? 'page' : undefined}
              aria-label={
                item.isExternalLink
                  ? `${item.label}`
                  : item.ariaLabel || item.label
              }
            >
              <NavItemContent item={item} hideIcon={!isExpanded} />
            </Link>
          );
        }
        return <NavItemLink item={item} isExpanded={isExpanded}></NavItemLink>;
      },
      resourcesOptions: [
        {
          iconSrc: chatIcon,
          label: intl.get(
            `SIDEBAR.SUPPORT_POPUP_OPTIONS.${SUPPORT_POPUP_OPTIONS.CHAT}`
          ),
          onClick: () => {
            showMessenger();
          },
        },
        {
          iconSrc: albumIcon,
          label: intl.get(
            `SIDEBAR.SUPPORT_POPUP_OPTIONS.${SUPPORT_POPUP_OPTIONS.LEARNING}`
          ),
          isLink: true,
          href: HELPER_CENTER_LINK,
          isExternalLink: true,
        },
        {
          label: intl.get(
            `SIDEBAR.SUPPORT_POPUP_OPTIONS.${SUPPORT_POPUP_OPTIONS.LEARNOPS_CERTIFICATION}`
          ),
          iconSrc: certIcon,
          isExternalLink: true,
          isLink: true,
          href: 'https://learnops.academy',
        },
        {
          label: intl.get(
            `SIDEBAR.SUPPORT_POPUP_OPTIONS.${SUPPORT_POPUP_OPTIONS.LEARNOPS_COMMUNITY}`
          ),
          iconSrc: communityIcon,
          isExternalLink: true,
          isLink: true,
          href: 'https://learnops.com',
        },
      ],
    };
  }, [itemGroups]);

  return appSideNavProps;
};

export default useAppSideNav;
